// Algolia limits the amount of nested AND/OR clauses that can be used in
// a single query so in order to get all products for a category or
// featured section (e.g. Twic Picks) across all wallets we have to use
// algoliaClient.multipleQueries() which results in the possibility
// of returning duplicates if multiple wallets are eligible for
// the same product (i.e. common categories/subcategories between wallets).
// To prevent the user from seeing the duplicates we filter them out before
// rendering the UI in common/ProductList. This works well enough, but will
// become somewhat problematic when showing search results where the total
// product count is shown before fetching all of the products themselves.
// We'll probably need to address this at some point in the future.

import type { SearchResponse } from '@algolia/client-search';
import { bboxPolygon } from '@turf/turf';
import dayjs from 'dayjs';
import { chain, uniqBy, isEmpty } from 'lodash';

import { IProduct } from 'apis/algolia';
import { CategorySortValue } from 'constants/categories';
import {
  FEATURED_PRODUCTS,
  FEATURED_SECTIONS,
  FEATURED_SUBSCRIPTIONS,
} from 'constants/products';
import { getConversionToUSD } from 'utils/currency';
import { mapCategoriesToSubcategories } from 'utils/product';

import { getExpiringFundsAmount, getFunds } from './wallet';

type EmployeeWallet = {
  company_wallet_configuration: {
    blacklisted_vendors: Array<string>;
    categories: Array<{
      id: string;
      name: string;
      subcategories: Array<{
        eligible: boolean;
        value: string;
      }>;
    }>;
  };
};

export type FacetFilters =
  | ReadonlyArray<string>
  | ReadonlyArray<ReadonlyArray<string> | string>;
export type NumericFilters =
  | ReadonlyArray<string>
  | ReadonlyArray<ReadonlyArray<string>>;

type SearchParams = {
  aroundLatLng?: string;
  aroundRadius?: number;
  facetFilters: Array<Array<string>>;
  filters: string;
  insidePolygon?: Array<Array<number>>;
  hitsPerPage: number;
  page: number;
};

export const not = (value: string) => `-${value}`;

export const getProductsQueryParams = ({
  employee_wallet,
  country,
  subcategories,
  facet_filters = [],
  numeric_filters = [],
}: {
  employee_wallet: EmployeeWallet;
  country: string;
  subcategories?: string[];
  facet_filters?: FacetFilters;
  numeric_filters?: NumericFilters;
}) => {
  const vendor_facet_filters =
    employee_wallet.company_wallet_configuration.blacklisted_vendors.map(
      (vid: string) => `vendor_id:${not(vid)}`
    );
  const subcats =
    subcategories ??
    mapCategoriesToSubcategories(
      employee_wallet.company_wallet_configuration.categories
    );
  const subcategory_facet_filters = !subcats.length
    ? ['subcategories:no_categories']
    : subcats.map((subcat: string) => `subcategories:${subcat}`);

  const base_facet_filters = [
    'is_product_active:true',
    'is_vendor_active:true',
    `available_countries:${country}`,
    ...vendor_facet_filters,
    subcategory_facet_filters,
  ];

  return {
    facet_filters: [...base_facet_filters, ...facet_filters],
    numeric_filters,
  };
};

export const groupAndCountResultsForMultipleWallets = <T = unknown>(
  results: Array<SearchResponse<T>>
) =>
  results.reduce<{ items: Array<T>; count: number; pages: number }>(
    (acc, result) => {
      acc.items.push(...result.hits);
      acc.count += result.nbHits;
      acc.pages = Math.max(acc.pages, result.nbPages);
      return acc;
    },
    { items: [], count: 0, pages: 0 }
  );

export const getProductsWithCategoriesQueryParams = ({
  category_id,
  country,
  employee_wallets,
  employee_wallet,
  facet_filters = [],
  numeric_filters = [],
  subcategories = [],
}: {
  category_id: string;
  country: string;
  employee_wallets: Array<EmployeeWallet>;
  employee_wallet?: EmployeeWallet;
  facet_filters?: FacetFilters;
  numeric_filters?: NumericFilters;
  subcategories?: string[];
}) => {
  const wallets = employee_wallet ? [employee_wallet] : employee_wallets;
  const employee_wallets_with_category = wallets.filter((employee_wallet) =>
    employee_wallet.company_wallet_configuration.categories.find(
      ({ id }) => id === category_id
    )
  );

  return employee_wallets_with_category.map((employee_wallet) => {
    const category =
      employee_wallet.company_wallet_configuration.categories.find(
        ({ id }) => id === category_id
      );
    const all_subcategories =
      category?.subcategories.map(({ value }) => value).filter((sc) => sc) ??
      [];
    return getProductsQueryParams({
      employee_wallet,
      country,
      subcategories: isEmpty(subcategories) ? all_subcategories : subcategories,
      facet_filters,
      numeric_filters,
    });
  });
};

export const getFeaturedSectionFilters = ({
  data = [],
  employee_wallet,
  exchange_rate,
  featured_section,
}: {
  data?: Array<string>;
  employee_wallet?: EmployeeWallet & {
    amount: number;
    next_renew_amount_loss?: number;
  };
  exchange_rate?: number;
  featured_section: string;
}): {
  facet_filters: FacetFilters;
  numeric_filters: NumericFilters;
} => {
  switch (featured_section) {
    case FEATURED_SECTIONS.EXPIRING_FUNDS: {
      return {
        facet_filters: [],
        numeric_filters:
          exchange_rate && employee_wallet
            ? [
                `usd_msrp <= ${getConversionToUSD({
                  exchange_rate,
                  value: getExpiringFundsAmount(employee_wallet),
                })}`,
              ]
            : [],
      };
    }
    case FEATURED_SECTIONS.NO_COST: {
      return {
        facet_filters: [],
        numeric_filters:
          exchange_rate && employee_wallet
            ? [
                `usd_msrp <= ${getConversionToUSD({
                  exchange_rate,
                  value: getFunds(employee_wallet),
                })}`,
              ]
            : [],
      };
    }
    case FEATURED_SECTIONS.POPULAR: {
      return {
        facet_filters: [],
        numeric_filters: ['num_of_purchase > 10'],
      };
    }
    case FEATURED_SECTIONS.FEATURED_PRODUCTS: {
      const FEATURED_ITEMS = FEATURED_PRODUCTS.concat(FEATURED_SUBSCRIPTIONS);
      return {
        facet_filters: [
          FEATURED_ITEMS.map((object_id) => `objectID:${object_id}`),
        ],
        numeric_filters: [],
      };
    }
    case FEATURED_SECTIONS.VENDORS: {
      const facet_filters = data.map((vendor_id) => `vendor_id:${vendor_id}`);
      return {
        facet_filters: [facet_filters],
        numeric_filters: [],
      };
    }
    case FEATURED_SECTIONS.RECENCY: {
      return {
        facet_filters: [],
        numeric_filters: [
          `created_ms > ${dayjs().subtract(90, 'd').valueOf()}`,
        ],
      };
    }
    default:
      return {
        facet_filters: [],
        numeric_filters: [],
      };
  }
};

export const getVendorVenuesParams = ({
  activities,
  bounds,
  employee_wallets,
  hits_per_page = 200,
  location,
  page = 0,
}: {
  activities: Array<string>;
  bounds: string | Array<string> | null;
  employee_wallets: Array<EmployeeWallet>;
  hits_per_page?: number;
  location: {
    name: string | Array<string>;
    coordinates: {
      lat: string | number | Array<string> | null;
      long: string | number | Array<string> | null;
    };
  };
  page?: number;
}) => {
  const block_list = chain(employee_wallets)
    .map(
      ({ company_wallet_configuration: { blacklisted_vendors } }) =>
        blacklisted_vendors
    )
    .flatten()
    .compact()
    .map((vendor_id: string) => `NOT twic_vendor_id:${vendor_id}`)
    .join(' AND ')
    .value();

  const fitness_activities_subcategories_value_set = employee_wallets.reduce(
    (acc: Set<string>, wallet: EmployeeWallet) => {
      const fitness_values_set = acc;
      //iteration on categories
      wallet.company_wallet_configuration.categories.forEach(
        ({
          id,
          subcategories,
        }: {
          id: string;
          subcategories: Array<{
            eligible: boolean;
            value: string;
          }>;
        }) => {
          if (id === 'fitness_activities') {
            //iteration on subcategories
            subcategories.forEach(({ value }: { value: string }) => {
              fitness_values_set.add(value);
            });
          }
        }
      );
      return fitness_values_set;
    },
    new Set()
  );

  const primary_type_facetfilters = Array.from(
    fitness_activities_subcategories_value_set
  ).map((value) => `primary_type:${value}`);

  const params: SearchParams = {
    facetFilters: [['inactive:false'], primary_type_facetfilters],
    filters: block_list,
    hitsPerPage: hits_per_page,
    page,
  };

  if (bounds && typeof bounds === 'string') {
    const [minY, maxY, minX, maxX] = bounds
      .split(',')
      .map((bound) => parseFloat(bound));
    const coordinates_obj = bboxPolygon([minY, maxY, minX, maxX]);
    const coordinates: Array<number> = [];

    coordinates_obj.geometry!.coordinates[0].forEach((input) => {
      coordinates.push(input[1]);
      coordinates.push(input[0]);
    });

    params.insidePolygon = [coordinates];
  } else {
    const { coordinates } = location;
    const { lat, long } = coordinates;

    params.aroundLatLng = `${lat}, ${long}`;

    params.aroundRadius = 16000; //10 miles
  }

  if (!isEmpty(activities) && Array.isArray(params.facetFilters)) {
    const activites_filter = activities.map(
      (activity: string) => `activities:${activity}`
    );
    params.facetFilters.push(activites_filter);
  }

  return params;
};

export const removeDuplicateProducts = (products: IProduct[]): IProduct[] =>
  uniqBy(products, (product: { id: string }) => product.id);

const ALGOLIA_VENDOR_LIST_INDEX = 'vendors_list_v1';
const ALGOLIA_VENDOR_LIST_INDEX_SORTED = 'vendors_list_v1_created_desc';
const ALGOLIA_VENDOR_LIST_PRICE_ASC = 'vendors_list_v1_usd_special_price_asc';
const ALGOLIA_VENDOR_LIST_PRICE_DESC = 'vendors_list_v1_usd_special_price_desc';

export const getSortIndex = (sortBy = '') => {
  switch (sortBy) {
    case CategorySortValue.BEST_SELLERS:
      return ALGOLIA_VENDOR_LIST_INDEX;
    case CategorySortValue.NEW_ARRIVALS:
      return ALGOLIA_VENDOR_LIST_INDEX_SORTED;
    case CategorySortValue.PRICE_ASC:
      return ALGOLIA_VENDOR_LIST_PRICE_ASC;
    case CategorySortValue.PRICE_DESC:
      return ALGOLIA_VENDOR_LIST_PRICE_DESC;
    default:
      return ALGOLIA_VENDOR_LIST_INDEX_SORTED;
  }
};
